import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, FileUp, CheckCircle2, CheckCheck, Loader2 } from "lucide-react";

const ICON = { submission: FileUp, decision: CheckCircle2 };

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { setItems([]); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openItem = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/notifications/${n.id}/read`);
        setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread((u) => Math.max(0, u - 1));
        window.dispatchEvent(new Event("notif:refresh"));
      } catch { /* ignore */ }
    }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((x) => ({ ...x, read: true })));
      setUnread(0);
      window.dispatchEvent(new Event("notif:refresh"));
    } catch { /* ignore */ }
  };

  return (
    <div className="space-y-6 animate-fade-up max-w-3xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Notifications</h1>
          <p className="text-muted-foreground mt-1">
            {unread > 0 ? `${unread} unread alert${unread > 1 ? "s" : ""}` : "You're all caught up."}
          </p>
        </div>
        {unread > 0 && (
          <Button data-testid="notif-page-mark-all" onClick={markAll} variant="outline" className="gap-2">
            <CheckCheck className="h-4 w-4" /> Mark all read
          </Button>
        )}
      </div>

      {items === null ? (
        <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin brand-text" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 flex flex-col items-center text-center" data-testid="notif-page-empty">
          <Bell className="h-10 w-10 text-muted-foreground mb-3" />
          <p className="font-heading font-semibold">No notifications yet</p>
          <p className="text-sm text-muted-foreground">New submissions and decisions will show up here.</p>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="notif-page-list">
          {items.map((n) => {
            const Icon = ICON[n.kind] || Bell;
            return (
              <button
                key={n.id}
                data-testid={`notif-page-item-${n.id}`}
                onClick={() => openItem(n)}
                className={`w-full text-left flex gap-4 rounded-lg border p-4 transition-colors hover:bg-secondary/60 ${
                  n.read ? "border-border bg-card" : "border-[rgb(var(--brand)/0.4)] bg-[rgb(var(--brand)/0.06)]"
                }`}
              >
                <div className={`h-9 w-9 shrink-0 rounded-md flex items-center justify-center ${n.read ? "bg-secondary text-muted-foreground" : "brand-bg text-white"}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-sm ${n.read ? "font-medium text-foreground" : "font-semibold text-foreground"}`}>{n.title}</p>
                    <span className="text-[11px] text-muted-foreground shrink-0">{timeAgo(n.created_at)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">{n.message}</p>
                </div>
                {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-red-600" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
